import React, { useState, useEffect, useRef } from "react";
import { useSignUp, useAuth, useClerk } from "@clerk/clerk-react";

import { SignupForm } from "@/components/signup-form";
import { AuthPageShell } from "@/components/auth-page-shell";
import {
  EMAIL_MAX_LENGTH,
  getSignUpValidationError,
  normalizeSignUpFields,
  PASSWORD_MAX_LENGTH,
  PERSON_NAME_MAX_LENGTH,
  stripControlChars,
  USERNAME_MAX_LENGTH,
  VERIFICATION_CODE_MAX_LENGTH,
} from "@/utils/authFieldValidation";
import { checkUsernameAvailability } from "@/services/authApi";
import { optInMarketingEmails } from "@/services/notificationsApi";
import { resolveSafeRedirectPath } from "../../lib/sessionExpired";
import { isDemoClaimReturnPath } from "@/utils/demoEntryAnalysisSession";

function getPostSignUpPath(): string {
  const redirectUrl = new URLSearchParams(window.location.search).get("redirect_url");
  return resolveSafeRedirectPath(redirectUrl);
}

function isClerkRateLimitError(err: unknown): boolean {
  const e = err as {
    status?: number
    errors?: Array<{ code?: string; message?: string; longMessage?: string }>
    message?: string
  } | null
  const first = e?.errors?.[0]
  const haystack = `${e?.status ?? ""} ${first?.code ?? ""} ${first?.message ?? ""} ${first?.longMessage ?? ""} ${e?.message ?? ""}`.toLowerCase()
  return (
    e?.status === 429 ||
    haystack.includes("too many requests") ||
    haystack.includes("rate_limit") ||
    haystack.includes("too_many_requests")
  )
}

function isVerificationCodeMismatchError(err: unknown): boolean {
  const e = err as {
    errors?: Array<{ code?: string; message?: string; longMessage?: string }>
    message?: string
  } | null
  const first = e?.errors?.[0]
  const code = (first?.code || "").toLowerCase()
  const haystack = `${code} ${first?.longMessage || first?.message || e?.message || ""}`.toLowerCase()
  if (haystack.includes("expired") || code === "form_code_expired" || code === "verification_expired") {
    return false
  }
  return (
    haystack.includes("incorrect") ||
    haystack.includes("invalid") ||
    haystack.includes("wrong") ||
    code.includes("incorrect") ||
    code.includes("invalid")
  )
}

const SIGNUPS_DISABLED_MESSAGE =
  "Sign-ups are currently disabled. Please try again later."

/** Clerk Dashboard disabled email (or similar) — avoid leaking raw "is unknown" / dashboard URLs. */
function isSignUpsDisabledError(err: unknown): boolean {
  const e = err as {
    errors?: Array<{
      code?: string
      message?: string
      longMessage?: string
      meta?: { param_name?: string }
    }>
    message?: string
  } | null
  const first = e?.errors?.[0]
  const code = (first?.code || "").toLowerCase()
  const param = (first?.meta?.param_name || "").toLowerCase()
  const haystack =
    `${code} ${first?.message ?? ""} ${first?.longMessage ?? ""} ${e?.message ?? ""} ${param}`.toLowerCase()

  if (code === "form_param_unknown" && (param === "email_address" || haystack.includes("email_address"))) {
    return true
  }
  if (haystack.includes("ensure the appropriate settings are enabled in the clerk dashboard")) {
    return true
  }
  return false
}

function humanizeSignUpError(err: unknown): string {
  if (isSignUpsDisabledError(err)) {
    return SIGNUPS_DISABLED_MESSAGE
  }
  if (isClerkRateLimitError(err)) {
    return "Too many sign-up attempts. Please wait a moment and try again."
  }

  const e = err as {
    message?: string
    errors?: Array<{ code?: string; message?: string; longMessage?: string }>
  } | null
  const first = e?.errors?.[0]
  return (
    (first?.longMessage || first?.message || e?.message || "").trim() ||
    "An error occurred during sign-up. Please try again."
  )
}

function humanizeVerificationError(err: unknown): string {
  if (isClerkRateLimitError(err)) {
    return "Too many verification emails were requested. Wait a minute, then tap Resend code once — extra retries will keep you blocked."
  }

  const e = err as {
    message?: string
    errors?: Array<{ code?: string; message?: string; longMessage?: string }>
  } | null
  const first = e?.errors?.[0]
  const code = (first?.code || "").toLowerCase()
  const raw = (first?.longMessage || first?.message || e?.message || "").trim()
  const haystack = `${code} ${raw}`.toLowerCase()

  if (haystack.includes("expired") || code === "form_code_expired" || code === "verification_expired") {
    return "This verification code has expired. Tap Resend code for a new one, then enter it below."
  }

  if (isVerificationCodeMismatchError(err)) {
    return "That code doesn’t match. Check the email and try again."
  }

  return raw || "Invalid verification code. Please try again."
}

function SignUpLoadingShell() {
  return (
    <div
      className="relative z-[1] flex min-h-svh w-full items-center justify-center bg-muted dark:bg-content-canvas"
      aria-busy="true"
    />
  );
}

function SignUp() {
  const { isLoaded, isSignedIn } = useAuth();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const clerk = useClerk();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOAuthLoading] = useState<"oauth_google" | null>(null);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [resendNotice, setResendNotice] = useState("");
  const [codeInputInvalid, setCodeInputInvalid] = useState(false);
  /** Blocks the isSignedIn → /dashboard redirect while the OTP morph plays. */
  const holdRedirectForVerifyAnimRef = useRef(false);
  /** User left the verify step — do not auto-resume from the persisted Clerk SignUp. */
  const skipResumeUnverifiedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (holdRedirectForVerifyAnimRef.current || verificationSuccess) return;
    window.location.assign(getPostSignUpPath());
  }, [isLoaded, isSignedIn, verificationSuccess]);

  useEffect(() => {
    if (!signUp) return;
    if (skipResumeUnverifiedRef.current) return;

    const clerkEmail =
      typeof signUp.emailAddress === "string" ? signUp.emailAddress.trim() : "";

    if (
      signUp.status === "missing_requirements" &&
      (signUp.verifications?.emailAddress?.status === "unverified" ||
        signUp.verifications?.emailAddress?.status === "expired")
    ) {
      setPendingVerification(true);
      // Local React state is empty on refresh / resumed Clerk sign-up; recover email from Clerk.
      if (clerkEmail) {
        setEmail((prev) => (prev.trim() ? prev : clerkEmail));
      }
      if (signUp.verifications?.emailAddress?.status === "expired") {
        setError(
          "This verification code has expired. Tap Resend code for a new one, then enter it below."
        );
      }
      // Do not auto-send another code here — Clerk already emailed on create.
      // Extra prepareEmailAddressVerification calls hit Clerk's rate limit.
    } else if (signUp.status === "complete") {
      setPendingVerification(false);
    } else if (signUp.status === "abandoned") {
      setPendingVerification(false);
    }
  }, [signUp]);

  const verificationEmail =
    email.trim() ||
    (typeof signUp?.emailAddress === "string" ? signUp.emailAddress.trim() : "");

  /**
   * After session is active: push marketing contact to Resend when box was checked.
   * Retries briefly — webhook may create the DB user a moment after setActive (400 no-email otherwise).
   */
  const syncMarketingIfOptedIn = async () => {
    if (!marketingOptIn) return;
    const maxAttempts = 6;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await optInMarketingEmails("account_signup");
        return;
      } catch (err) {
        if (attempt === maxAttempts) {
          console.warn(
            "Marketing email opt-in after signup failed after retries (non-blocking):",
            err
          );
          return;
        }
        await new Promise((r) => setTimeout(r, 400 * attempt));
      }
    }
  };

  const handleOAuthSignUp = async (strategy: "oauth_google") => {
    if (!termsAccepted) {
      setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
      return;
    }
    if (!signUp) {
      setError("Sign-up is not available. Please try again later.");
      return;
    }
    setError("");
    setOAuthLoading(strategy);
    try {
      try {
        await signUp.update({
          unsafeMetadata: { marketing_opt_in: Boolean(marketingOptIn) },
        } as any);
      } catch {
        // ignore
      }

      await signUp.authenticateWithRedirect({
        strategy: strategy,
        redirectUrl: `${window.location.origin}${getPostSignUpPath()}`,
        redirectUrlComplete: `${window.location.origin}${getPostSignUpPath()}`,
      });
    } catch (err: any) {
      console.error(`${strategy} sign-up error:`, err);
      setError(
        isSignUpsDisabledError(err)
          ? SIGNUPS_DISABLED_MESSAGE
          : err.message || "Failed to sign up with Google. Please try again."
      );
      setOAuthLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResendNotice("");

    const normalized = normalizeSignUpFields({
      firstName,
      lastName,
      username,
      email,
      password,
      confirmPassword,
    });

    const validationError = getSignUpValidationError({
      ...normalized,
      termsAccepted,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const availability = await checkUsernameAvailability(normalized.username);
      if (!availability.available || availability.reason !== "available") {
        setError(
          availability.reason === "taken"
            ? "That username is already taken. Please choose another."
            : availability.reason === "invalid"
              ? "Please enter a valid username."
              : "Could not verify username availability. Please try again."
        );
        return;
      }
    } catch (err: unknown) {
      // Fail closed — do not create an account without a successful uniqueness check.
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        (err as { status?: number }).status === 429
      ) {
        setError("Too many username checks. Please wait a moment and try again.");
        return;
      }
      setError("Could not verify username availability. Please try again.");
      return;
    }

    // Persist normalized values in local state so verify UI shows the cleaned email.
    setFirstName(normalized.firstName);
    setLastName(normalized.lastName);
    setUsername(normalized.username);
    setEmail(normalized.email);

    setLoading(true);

    if (!signUp) {
      setError("Sign-up is not available. Please try again later.");
      setLoading(false);
      return;
    }

    const CREATE_TIMEOUT_MS = 45_000;
    let createTimeoutId = 0;

    try {
      skipResumeUnverifiedRef.current = false;
      const result = await Promise.race([
        signUp.create({
          emailAddress: normalized.email,
          password: normalized.password,
          firstName: normalized.firstName || undefined,
          lastName: normalized.lastName || undefined,
          username: normalized.username || undefined,
          // Must be on create so Clerk user.created webhook sees opt-in (not a later update race).
          unsafeMetadata: { marketing_opt_in: Boolean(marketingOptIn) },
        } as any),
        new Promise<never>((_, reject) => {
          createTimeoutId = window.setTimeout(() => {
            reject(
              new Error(
                "Sign-up is taking too long. Refresh the page and try again — if this keeps happening, disable ad blockers for this site or try another browser."
              )
            );
          }, CREATE_TIMEOUT_MS);
        }),
      ]);
      if (createTimeoutId) window.clearTimeout(createTimeoutId);

      try {
        await signUp.update({
          unsafeMetadata: { marketing_opt_in: Boolean(marketingOptIn) },
        } as any);
      } catch {
        // ignore — create already carried the flag when supported
      }

      if (result.status === "missing_requirements") {
        const needsEmailVerification =
          result.verifications?.emailAddress?.status === "unverified" ||
          (result.unverifiedFields || []).includes("email_address");

        if (needsEmailVerification) {
          try {
            await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
            setResendNotice("A verification code is on its way. Check inbox and spam.");
          } catch (prepareErr: unknown) {
            setError(humanizeVerificationError(prepareErr));
          }
          // Prefer the created sign-up email so the verify step always shows it.
          const createdEmail =
            (typeof signUp.emailAddress === "string" && signUp.emailAddress.trim()) ||
            (typeof result.emailAddress === "string" && result.emailAddress.trim()) ||
            normalized.email;
          setEmail(createdEmail);
          setPendingVerification(true);
        } else {
          let errorMessage = "Additional information is required to complete sign-up.";

          if (!username || username.trim() === "") {
            errorMessage = "Username is required. Please enter a username.";
          } else {
            const unverifiedFields = result.unverifiedFields || [];
            if (unverifiedFields.length > 0) {
              errorMessage = `Please verify: ${unverifiedFields.join(", ")}`;
            }
          }

          setError(errorMessage);
        }
      } else if (result.status === "complete") {
        if (result.createdSessionId) {
          try {
            if (!isSignedIn) {
              await clerk.setActive({ session: result.createdSessionId });
            }
            await syncMarketingIfOptedIn();
            window.location.assign(getPostSignUpPath());
          } catch (sessionError: any) {
            if (
              sessionError.errors?.[0]?.code === "session_exists" ||
              sessionError.message?.includes("session") ||
              sessionError.message?.includes("already")
            ) {
              await syncMarketingIfOptedIn();
              window.location.assign(getPostSignUpPath());
            } else {
              throw sessionError;
            }
          }
        } else if (isSignedIn) {
          await syncMarketingIfOptedIn();
          window.location.assign(getPostSignUpPath());
        } else {
          setError("Account created but session creation failed. Please sign in.");
        }
      } else {
        setError(`Sign-up incomplete. Status: ${result.status}. Please try again.`);
      }
    } catch (err: unknown) {
      setError(humanizeSignUpError(err));
    } finally {
      if (createTimeoutId) window.clearTimeout(createTimeoutId);
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResendNotice("");
    setCodeInputInvalid(false);
    setLoading(true);

    if (!signUp) {
      setError("Verification is not available. Please try again later.");
      setLoading(false);
      return;
    }

    let accepted = false;
    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status === "complete") {
        // Hold dashboard redirect *before* setActive flips isSignedIn (that effect is racey).
        holdRedirectForVerifyAnimRef.current = true;
        setVerificationSuccess(true);
        accepted = true;

        try {
          if (result.createdSessionId) {
            if (!isSignedIn) {
              await clerk.setActive({ session: result.createdSessionId });
            }
            await syncMarketingIfOptedIn();
          } else if (!isSignedIn) {
            holdRedirectForVerifyAnimRef.current = false;
            setVerificationSuccess(false);
            accepted = false;
            setError("Verification successful but session creation failed. Please sign in.");
          } else {
            await syncMarketingIfOptedIn();
          }
        } catch (sessionError: any) {
          const sessionAlreadyOk =
            sessionError.errors?.[0]?.code === "session_exists" ||
            sessionError.message?.includes("session") ||
            sessionError.message?.includes("already");
          if (!sessionAlreadyOk) {
            holdRedirectForVerifyAnimRef.current = false;
            setVerificationSuccess(false);
            accepted = false;
            throw sessionError;
          }
          await syncMarketingIfOptedIn();
        }
      } else {
        setCodeInputInvalid(true);
        setError("Verification code is incorrect. Please try again.");
      }
    } catch (err: any) {
      holdRedirectForVerifyAnimRef.current = false;
      setVerificationSuccess(false);
      setCodeInputInvalid(isVerificationCodeMismatchError(err));
      setError(humanizeVerificationError(err));
    } finally {
      if (!accepted) {
        setLoading(false);
      }
    }
  };

  const handleVerificationSuccessComplete = () => {
    holdRedirectForVerifyAnimRef.current = false;
    window.location.assign(getPostSignUpPath());
  };

  const handleResendCode = async () => {
    if (!signUp || verificationSuccess) return;
    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setError("");
      setCodeInputInvalid(false);
      setResendNotice("A new code is on its way. Check inbox and spam.");
    } catch (err) {
      const msg = humanizeVerificationError(err);
      setResendNotice("");
      setCodeInputInvalid(false);
      setError(
        msg === "Invalid verification code. Please try again."
          ? "Failed to resend code. Please try again."
          : msg
      );
    }
  };

  const handleAbandonVerification = async () => {
    skipResumeUnverifiedRef.current = true;
    setPendingVerification(false);
    setVerificationCode("");
    setVerificationSuccess(false);
    setError("");
    setResendNotice("");
    setCodeInputInvalid(false);
    try {
      if (signUp) {
        await signUp.create({});
      }
    } catch (err) {
      console.warn("Could not reset Clerk sign-up; continuing with the form.", err);
    }
  };

  if (!isLoaded || !signUpLoaded) {
    return <SignUpLoadingShell />;
  }

  const demoEntryAnalysisGate = isDemoClaimReturnPath(
    new URLSearchParams(window.location.search).get("redirect_url")
  );

  return (
    <AuthPageShell>
        <SignupForm
          pendingVerification={pendingVerification}
          email={verificationEmail || email}
          firstName={firstName}
          lastName={lastName}
          username={username}
          password={password}
          confirmPassword={confirmPassword}
          verificationCode={verificationCode}
          marketingOptIn={marketingOptIn}
          termsAccepted={termsAccepted}
          loading={loading}
          verificationSuccess={verificationSuccess}
          onVerificationSuccessComplete={handleVerificationSuccessComplete}
          demoEntryAnalysisGate={demoEntryAnalysisGate}
          oauthLoading={oauthLoading}
          error={error}
          resendNotice={resendNotice}
          codeInputInvalid={codeInputInvalid}
          onEmailChange={(value) => setEmail(stripControlChars(value).slice(0, EMAIL_MAX_LENGTH))}
          onFirstNameChange={(value) =>
            setFirstName(stripControlChars(value).slice(0, PERSON_NAME_MAX_LENGTH))
          }
          onLastNameChange={(value) =>
            setLastName(stripControlChars(value).slice(0, PERSON_NAME_MAX_LENGTH))
          }
          onUsernameChange={(value) =>
            setUsername(
              stripControlChars(value)
                .replace(/[^a-zA-Z0-9_]/g, "")
                .slice(0, USERNAME_MAX_LENGTH)
            )
          }
          onPasswordChange={(value) => setPassword(value.slice(0, PASSWORD_MAX_LENGTH))}
          onConfirmPasswordChange={(value) =>
            setConfirmPassword(value.slice(0, PASSWORD_MAX_LENGTH))
          }
          onVerificationCodeChange={(value) =>
            setVerificationCode(stripControlChars(value).slice(0, VERIFICATION_CODE_MAX_LENGTH))
          }
          onMarketingOptInChange={setMarketingOptIn}
          onTermsAcceptedChange={setTermsAccepted}
          onSubmit={handleSubmit}
          onVerificationSubmit={handleVerification}
          onResendCode={handleResendCode}
          onAbandonVerification={handleAbandonVerification}
          onOAuthSignUp={handleOAuthSignUp}
        />
    </AuthPageShell>
  );
}

export default SignUp;
